import React, {useEffect, useRef} from 'react';
import {Animated, Easing, StyleSheet, View} from 'react-native';
import CCText from '@/Atoms/CCText';

// Every card below must describe something the Clubs feature ACTUALLY ships.
// Scoring copy in particular is load-bearing: clubs/services/scoring.py fixes
// EFFECTIVE_RULES = {game: 0, puzzle: 2, login: 0} and CHALLENGE_POINTS =
// {win: 10, draw: 0, loss: 0} — regular games and logins score NOTHING, so
// never promise points for "playing" or "showing up".
const ACTIVITIES = [
  {
    icon: '♟',
    tone: '#E7F6F3',
    accent: '#23796F',
    title: 'Challenge clubmates',
    body: 'Post a game in chat, take a rival head-on, and bank 10 club points for the win.',
  },
  {
    icon: '⚡',
    tone: '#FFF2C7',
    accent: '#986A00',
    title: 'Race in Puzzle Royal',
    body: 'Admins launch a Puzzle Royal and the whole club joins the race straight from chat.',
  },
  {
    icon: '♜',
    tone: '#EAF1FF',
    accent: '#365FAD',
    title: 'Play club tournaments',
    body: 'Swiss and Arena events scheduled for your club, with the next one pinned up top.',
  },
  {
    icon: '◆',
    tone: '#F4EAFE',
    accent: '#70459A',
    title: 'Solve to climb',
    body: 'Every puzzle you crack adds 2 points to your club and moves you up the board.',
  },
  {
    icon: '●',
    tone: '#FFEDEA',
    accent: '#B64A35',
    title: 'Always in the loop',
    body: 'Chat, announcements, and quick polls keep the whole chess crew connected.',
  },
  {
    icon: '★',
    tone: '#EAF7E7',
    accent: '#447F38',
    title: 'Measure up',
    body: 'Compare with any member, track top contributors, and see how your club ranks.',
  },
];

// Mirrors the two things that actually score, then where they land.
const MOMENTS = [
  {label: 'SOLVE', text: 'Puzzles · 2 pts'},
  {label: 'WIN', text: 'Challenges · 10 pts'},
  {label: 'CLIMB', text: 'Club leaderboards'},
];

const ANIMATIONS_ENABLED = process.env.NODE_ENV !== 'test';

type Props = {
  isWide?: boolean;
  isMember?: boolean;
  memberCount?: number;
};

export default function ClubExperience({
  isWide = false,
  isMember = false,
  memberCount = 0,
}: Props) {
  const intro = useRef(new Animated.Value(ANIMATIONS_ENABLED ? 0 : 1)).current;
  const cards = useRef(new Animated.Value(ANIMATIONS_ENABLED ? 0 : 1)).current;
  const footer = useRef(new Animated.Value(ANIMATIONS_ENABLED ? 0 : 1)).current;
  const float = useRef(new Animated.Value(0)).current;
  const pulse = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!ANIMATIONS_ENABLED) {
      return;
    }
    const entrance = Animated.stagger(110, [
      Animated.timing(intro, {
        toValue: 1,
        duration: 420,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
        isInteraction: false,
      }),
      Animated.timing(cards, {
        toValue: 1,
        duration: 460,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
        isInteraction: false,
      }),
      Animated.timing(footer, {
        toValue: 1,
        duration: 420,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
        isInteraction: false,
      }),
    ]);
    const floatLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(float, {
          toValue: 1,
          duration: 1800,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
          isInteraction: false,
        }),
        Animated.timing(float, {
          toValue: 0,
          duration: 1800,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
          isInteraction: false,
        }),
      ]),
    );
    const pulseLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 800,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
          isInteraction: false,
        }),
        Animated.timing(pulse, {
          toValue: 0,
          duration: 800,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
          isInteraction: false,
        }),
      ]),
    );

    entrance.start();
    floatLoop.start();
    pulseLoop.start();
    return () => {
      entrance.stop();
      floatLoop.stop();
      pulseLoop.stop();
    };
  }, [cards, float, footer, intro, pulse]);

  const enterStyle = (value: Animated.Value, distance = 14) => ({
    opacity: value,
    transform: [
      {
        translateY: value.interpolate({
          inputRange: [0, 1],
          outputRange: [distance, 0],
        }),
      },
    ],
  });
  const floatY = float.interpolate({
    inputRange: [0, 1],
    outputRange: [0, -7],
  });
  const pulseScale = pulse.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.65],
  });
  const pulseOpacity = pulse.interpolate({
    inputRange: [0, 1],
    outputRange: [0.5, 0],
  });

  return (
    <View
      testID="club-experience"
      style={[styles.shell, isWide && styles.shellWide]}>
      <View pointerEvents="none" style={styles.decorations}>
        <Animated.View
          style={[
            styles.floatingPiece,
            styles.floatingKnight,
            {transform: [{translateY: floatY}, {rotate: '-10deg'}]},
          ]}>
          <CCText style={styles.floatingPieceText}>♞</CCText>
        </Animated.View>
        <Animated.View
          style={[
            styles.floatingPiece,
            styles.floatingPawn,
            {
              transform: [
                {
                  translateY: float.interpolate({
                    inputRange: [0, 1],
                    outputRange: [-4, 3],
                  }),
                },
                {rotate: '12deg'},
              ],
            },
          ]}>
          <CCText style={styles.floatingPieceText}>♙</CCText>
        </Animated.View>
      </View>

      <Animated.View style={enterStyle(intro)}>
        <View style={styles.liveRow}>
          <View style={styles.liveDotWrap}>
            <Animated.View
              style={[
                styles.livePulse,
                {opacity: pulseOpacity, transform: [{scale: pulseScale}]},
              ]}
            />
            <View style={styles.liveDot} />
          </View>
          <CCText style={styles.eyebrow}>
            {isMember ? 'YOUR CLUB, IN MOTION' : 'MORE THAN A CHESS GROUP'}
          </CCText>
        </View>
        <CCText fontWeight="thunder" style={styles.title}>
          Your chess crew. Your next move.
        </CCText>
        <CCText style={styles.intro}>
          Clubs are where chess becomes a team sport. Challenge the players who
          match your energy, race them through puzzles and tournaments, and turn
          everyday progress into something the whole club can celebrate.
        </CCText>
        <View style={styles.promisePill}>
          <CCText style={styles.promiseText}>
            {memberCount > 1
              ? `${memberCount} players · one shared climb`
              : 'Challenge · Solve · Race · Climb'}
          </CCText>
        </View>
      </Animated.View>

      <Animated.View
        style={[
          styles.activityGrid,
          isWide && styles.activityGridWide,
          enterStyle(cards, 18),
        ]}>
        {ACTIVITIES.map(activity => (
          <View
            key={activity.title}
            style={[styles.activityCard, isWide && styles.activityCardWide]}>
            <View style={[styles.iconTile, {backgroundColor: activity.tone}]}>
              <CCText style={[styles.iconText, {color: activity.accent}]}>
                {activity.icon}
              </CCText>
            </View>
            <View style={styles.activityCopy}>
              <CCText style={styles.activityTitle}>{activity.title}</CCText>
              <CCText style={styles.activityBody}>{activity.body}</CCText>
            </View>
          </View>
        ))}
      </Animated.View>

      <Animated.View style={[styles.rhythmCard, enterStyle(footer)]}>
        <View style={styles.rhythmHeadingRow}>
          <View>
            <CCText style={styles.rhythmEyebrow}>THE CLUB RHYTHM</CCText>
            <CCText style={styles.rhythmTitle}>
              There is always a move to make
            </CCText>
          </View>
          <CCText style={styles.rhythmSpark}>✦</CCText>
        </View>
        <View style={[styles.moments, !isWide && styles.momentsStacked]}>
          {MOMENTS.map((moment, index) => (
            <React.Fragment key={moment.label}>
              <View style={styles.moment}>
                <CCText style={styles.momentLabel}>{moment.label}</CCText>
                <CCText style={styles.momentText}>{moment.text}</CCText>
              </View>
              {index < MOMENTS.length - 1 ? (
                <CCText
                  style={[
                    styles.momentArrow,
                    !isWide && styles.momentArrowStacked,
                  ]}>
                  →
                </CCText>
              ) : null}
            </React.Fragment>
          ))}
        </View>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    overflow: 'hidden',
    position: 'relative',
    marginBottom: 12,
    borderRadius: 20,
    padding: 18,
    backgroundColor: '#233E3A',
    borderWidth: 1,
    borderColor: '#365B55',
  },
  shellWide: {
    padding: 22,
    marginTop: 0,
    marginBottom: 20,
  },
  decorations: {
    ...StyleSheet.absoluteFillObject,
  },
  floatingPiece: {
    position: 'absolute',
    width: 54,
    height: 54,
    borderRadius: 27,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.055)',
  },
  floatingKnight: {
    top: 14,
    right: 14,
  },
  floatingPawn: {
    top: 120,
    left: -20,
  },
  floatingPieceText: {
    color: 'rgba(255,255,255,0.16)',
    fontSize: 29,
  },
  liveRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 9,
  },
  liveDotWrap: {
    width: 10,
    height: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  livePulse: {
    position: 'absolute',
    width: 9,
    height: 9,
    borderRadius: 5,
    backgroundColor: '#76E2BF',
  },
  liveDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: '#76E2BF',
  },
  eyebrow: {
    color: '#9FE4CE',
    fontSize: 10.5,
    fontWeight: '800',
    letterSpacing: 1.15,
  },
  title: {
    maxWidth: 390,
    color: '#FFF8E8',
    fontSize: 27,
    lineHeight: 30,
    marginBottom: 10,
  },
  intro: {
    maxWidth: 465,
    color: '#D6E5E0',
    fontSize: 13.5,
    lineHeight: 20,
  },
  promisePill: {
    alignSelf: 'flex-start',
    marginTop: 13,
    paddingHorizontal: 11,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(250,207,71,0.13)',
    borderWidth: 1,
    borderColor: 'rgba(250,207,71,0.32)',
  },
  promiseText: {
    color: '#FFE28A',
    fontSize: 11.5,
    fontWeight: '700',
  },
  activityGrid: {
    marginTop: 19,
    gap: 9,
  },
  activityGridWide: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  activityCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 11,
    padding: 12,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.075)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  activityCardWide: {
    width: '48.9%',
    minHeight: 116,
  },
  iconTile: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconText: {
    fontSize: 19,
    fontWeight: '800',
  },
  activityCopy: {
    flex: 1,
  },
  activityTitle: {
    color: '#FFF8E8',
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 4,
  },
  activityBody: {
    color: '#BED0CB',
    fontSize: 11.5,
    lineHeight: 16.5,
  },
  rhythmCard: {
    marginTop: 12,
    padding: 14,
    borderRadius: 15,
    backgroundColor: '#F7E6A7',
  },
  rhythmHeadingRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  rhythmEyebrow: {
    color: '#826415',
    fontSize: 9,
    fontWeight: '800',
    letterSpacing: 1,
    marginBottom: 3,
  },
  rhythmTitle: {
    color: '#3E3425',
    fontSize: 13.5,
    fontWeight: '700',
  },
  rhythmSpark: {
    color: '#9B7616',
    fontSize: 20,
  },
  moments: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  momentsStacked: {
    alignItems: 'stretch',
    flexDirection: 'column',
  },
  moment: {
    flex: 1,
  },
  momentLabel: {
    color: '#836615',
    fontSize: 8.5,
    fontWeight: '800',
    letterSpacing: 0.8,
  },
  momentText: {
    color: '#4D3F37',
    fontSize: 11.5,
    fontWeight: '600',
    marginTop: 2,
  },
  momentArrow: {
    color: '#9D7E2D',
    fontSize: 16,
    marginHorizontal: 7,
  },
  momentArrowStacked: {
    transform: [{rotate: '90deg'}],
    alignSelf: 'flex-start',
    marginHorizontal: 14,
    marginVertical: 3,
  },
});
